import { Container, Graphics, Sprite } from 'pixi.js-legacy';
import type { Item } from '../../entities/Item';
import type { ItemType } from '../../types';
import { svgTextureCache } from '../utils/SvgTextureCache';

/**
 * Item Sprite/Graphics 오브젝트 풀
 * SVG 텍스쳐가 로드되어 있으면 Sprite를 사용, 아니면 Graphics로 폴백
 */
class ItemPool {
    private pool: Map<ItemType, (Sprite | Graphics)[]> = new Map();
    private active: Map<Item, Sprite | Graphics> = new Map();
    private container: Container;
    // iOS: 공격적인 풀 크기 제한 (각 타입당)
    private maxPoolSizePerType: number = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 5 : 10;
    // SVG viewBox: 60x60, 아이콘도 약 40~50px 크기로 그려져 있음
    // scale = (item.radius * 2) / 60 으로 원하는 크기 계산
    // radius=15: scale=0.5 → 30px, radius=20: scale=0.67 → 40px
    private readonly SVG_SIZE = 60;

    constructor(container: Container) {
        this.container = container;
        // 각 타입별 풀 초기화
        const types: ItemType[] = ['heart', 'star', 'lightning', 'clock', 'bomb', 'magnet'];
        for (const type of types) {
            this.pool.set(type, []);
        }
    }

    get(item: Item): Sprite | Graphics {
        const typePool = this.pool.get(item.type) || [];
        let displayObj = typePool.pop();

        if (!displayObj) {
            displayObj = this.createItemDisplayObject(item.type, item.radius);
        }

        displayObj.position.set(item.x, item.y);
        displayObj.rotation = item.rotation;
        displayObj.visible = true;

        this.active.set(item, displayObj);
        this.container.addChild(displayObj);

        return displayObj;
    }

    release(item: Item): void {
        const displayObj = this.active.get(item);
        if (displayObj) {
            displayObj.visible = false;
            this.container.removeChild(displayObj);
            this.active.delete(item);

            const typePool = this.pool.get(item.type);
            if (typePool) {
                // 풀 크기 제한 초과 시 destroy
                if (typePool.length < this.maxPoolSizePerType) {
                    typePool.push(displayObj);
                } else {
                    displayObj.destroy();
                }
            }
        }
    }

    update(item: Item, audioIntensity: number = 0): void {
        const displayObj = this.active.get(item);
        if (displayObj) {
            const pulseScale = 1 + Math.sin(item.pulsePhase) * 0.1;
            const audioScale = 1 + audioIntensity * 0.3;
            // Sprite(SVG): 60x60 SVG를 item.radius*2 크기로 스케일
            // Graphics: 이미 item.radius 크기로 그려져서 scale 1
            const isSprite = displayObj instanceof Sprite;
            const baseScale = isSprite
                ? (item.radius * 2) / this.SVG_SIZE
                : 1;
            const scale = baseScale * pulseScale * audioScale;

            displayObj.position.set(item.x, item.y);
            displayObj.rotation = item.rotation;
            displayObj.scale.set(scale);
        }
    }

    has(item: Item): boolean {
        return this.active.has(item);
    }

    /**
     * Sprite(SVG) 또는 Graphics 오브젝트 생성
     */
    private createItemDisplayObject(type: ItemType, size: number): Sprite | Graphics {
        // SVG 텍스쳐가 로드되어 있으면 Sprite 사용
        if (svgTextureCache.isLoaded()) {
            const sprite = svgTextureCache.createItemSprite(type);
            if (sprite) {
                return sprite;
            }
        }

        // 폴백: Graphics로 직접 그리기
        const g = new Graphics();
        this.drawItemShape(g, type, size);
        return g;
    }

    private drawItemShape(g: Graphics, type: ItemType, size: number): void {
        g.lineStyle(2, 0xFFFFFF);

        switch (type) {
            case 'heart':
                this.drawHeart(g, size);
                break;
            case 'star':
                this.drawStar(g, size);
                break;
            case 'lightning':
                this.drawLightning(g, size);
                break;
            case 'clock':
                this.drawClock(g, size);
                break;
            case 'bomb':
                this.drawBomb(g, size);
                break;
            case 'magnet':
                this.drawMagnet(g, size);
                break;
        }
    }

    private drawHeart(g: Graphics, size: number): void {
        const scale = size / 10;

        // 하트 왼쪽
        g.moveTo(0, -3 * scale);
        g.bezierCurveTo(-5 * scale, -8 * scale, -10 * scale, -3 * scale, 0, 5 * scale);

        // 하트 오른쪽
        g.moveTo(0, -3 * scale);
        g.bezierCurveTo(5 * scale, -8 * scale, 10 * scale, -3 * scale, 0, 5 * scale);
    }

    private drawStar(g: Graphics, size: number): void {
        const spikes = 5;
        const outerRadius = size;
        const innerRadius = size * 0.5;
        const points: number[] = [];

        for (let i = 0; i < spikes * 2; i++) {
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const angle = (i * Math.PI) / spikes - Math.PI / 2;
            points.push(Math.cos(angle) * radius, Math.sin(angle) * radius);
        }

        g.drawPolygon(points);
    }

    private drawLightning(g: Graphics, size: number): void {
        const scale = size / 10;
        const points = [
            2 * scale, -8 * scale,
            -2 * scale, -2 * scale,
            1 * scale, -2 * scale,
            -3 * scale, 8 * scale,
            0 * scale, 1 * scale,
            -2 * scale, 1 * scale,
            2 * scale, -8 * scale
        ];

        g.drawPolygon(points);
    }

    private drawClock(g: Graphics, size: number): void {
        // 원형 시계
        g.drawCircle(0, 0, size * 0.8);

        // 시침
        g.moveTo(0, 0);
        g.lineTo(0, -size * 0.4);

        // 분침
        g.moveTo(0, 0);
        g.lineTo(size * 0.5, 0);

        // 중심점
        g.drawCircle(0, 0, size * 0.15);
    }

    private drawBomb(g: Graphics, size: number): void {
        // 폭탄 몸체 (타원형 - 클래식 폭탄 스타일)
        g.drawEllipse(0, size * 0.15, size * 0.65, size * 0.7);

        // 폭탄 상단 목 부분 (사다리꼴)
        g.moveTo(-size * 0.25, -size * 0.5);
        g.lineTo(-size * 0.15, -size * 0.7);
        g.lineTo(size * 0.15, -size * 0.7);
        g.lineTo(size * 0.25, -size * 0.5);

        // 폭탄 목 상단 고리
        g.drawCircle(0, -size * 0.75, size * 0.12);

        // 도화선 (베지어 곡선)
        g.moveTo(0, -size * 0.87);
        g.bezierCurveTo(
            size * 0.2, -size * 1.0,
            size * 0.35, -size * 1.05,
            size * 0.25, -size * 1.25
        );

        // 중심 불꽃
        g.drawCircle(size * 0.25, -size * 1.25, size * 0.15);

        // 불꽃 스파크 라인들 (6방향)
        const sparkX = size * 0.25;
        const sparkY = -size * 1.25;
        const innerR = size * 0.18;
        const outerR = size * 0.35;
        for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3;
            g.moveTo(
                sparkX + Math.cos(angle) * innerR,
                sparkY + Math.sin(angle) * innerR
            );
            g.lineTo(
                sparkX + Math.cos(angle) * outerR,
                sparkY + Math.sin(angle) * outerR
            );
        }

        // 폭탄 몸체 하이라이트 (광택 효과)
        g.drawCircle(-size * 0.25, -size * 0.1, size * 0.15);

        // 폭탄 몸체 밴드 (가로 줄)
        g.moveTo(-size * 0.6, size * 0.3);
        g.quadraticCurveTo(0, size * 0.4, size * 0.6, size * 0.3);
    }

    private drawMagnet(g: Graphics, size: number): void {
        // U자형 자석
        g.arc(0, 0, size * 0.6, Math.PI, 0, false);

        // 왼쪽 다리
        g.moveTo(-size * 0.6, 0);
        g.lineTo(-size * 0.6, size * 0.7);

        // 오른쪽 다리
        g.moveTo(size * 0.6, 0);
        g.lineTo(size * 0.6, size * 0.7);

        // 다리 끝 가로선
        g.moveTo(-size * 0.8, size * 0.7);
        g.lineTo(-size * 0.4, size * 0.7);

        g.moveTo(size * 0.4, size * 0.7);
        g.lineTo(size * 0.8, size * 0.7);
    }

    getActiveItems(): IterableIterator<Item> {
        return this.active.keys();
    }

    clear(): void {
        for (const [item, displayObj] of this.active) {
            displayObj.visible = false;
            this.container.removeChild(displayObj);
            const typePool = this.pool.get(item.type);
            if (typePool) {
                // 풀 크기 제한 초과 시 destroy
                if (typePool.length < this.maxPoolSizePerType) {
                    typePool.push(displayObj);
                } else {
                    displayObj.destroy();
                }
            }
        }
        this.active.clear();
    }

    reset(): void {
        // clearAll()로 이미 컨테이너에서 제거되었으므로 참조만 정리
        this.active.clear();
        // 풀의 모든 오브젝트 destroy
        for (const [, typePool] of this.pool) {
            for (const displayObj of typePool) {
                displayObj.destroy();
            }
            typePool.length = 0;
        }
    }

    destroy(): void {
        this.clear();
        for (const [, typePool] of this.pool) {
            for (const displayObj of typePool) {
                displayObj.destroy();
            }
        }
        this.pool.clear();
    }
}

/**
 * Item 어댑터
 */
export class ItemAdapter {
    private itemPool: ItemPool;

    constructor(container: Container) {
        this.itemPool = new ItemPool(container);
    }

    /**
     * Set 생성 없이 직접 동기화하여 메모리 할당 최소화
     */
    sync(items: Item[], audioIntensity: number = 0): void {
        // 1. 죽은 아이템만 release
        for (const item of this.itemPool.getActiveItems()) {
            if (!item.isAlive) {
                this.itemPool.release(item);
            }
        }

        // 2. 살아있는 아이템 업데이트/추가
        for (const item of items) {
            if (this.itemPool.has(item)) {
                this.itemPool.update(item, audioIntensity);
            } else {
                this.itemPool.get(item);
                this.itemPool.update(item, audioIntensity);
            }
        }
    }

    clear(): void {
        this.itemPool.clear();
    }

    reset(): void {
        this.itemPool.reset();
    }

    destroy(): void {
        this.itemPool.destroy();
    }
}
